import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { AlertTriangle, Sparkles, CalendarClock, GraduationCap, Truck, History, ChevronRight, UserPlus, ListPlus, ArrowLeftRight, PackagePlus, ClipboardList, PackageSearch } from 'lucide-react';

import { operationsApi } from '../../api/operations.js';
import { replenishmentApi } from '../../api/replenishment.js';
import { useAuth } from '../../auth/AuthProvider.jsx';
import { Card } from '../../components/ui/Card.jsx';
import { Skeleton } from '../../components/ui/Skeleton.jsx';

function greeting() {
  const h = new Date().getHours();
  if (h < 12) return 'Buongiorno';
  if (h < 18) return 'Buon pomeriggio';
  return 'Buonasera';
}

function relTime(at) {
  if (!at) return '';
  const diff = Date.now() - new Date(at).getTime();
  const m = Math.round(diff / 60000);
  if (m < 1) return 'ora';
  if (m < 60) return `${m} min fa`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h} h fa`;
  return new Date(at).toLocaleDateString('it-IT');
}

const TONES = {
  sage: { text: 'text-brand', soft: 'bg-brand-soft', dot: 'bg-brand' },
  taupe: { text: 'text-accent', soft: 'bg-surface-subtle', dot: 'bg-accent' },
  amber: { text: 'text-warning', soft: 'bg-warning/10', dot: 'bg-warning' },
  slate: { text: 'text-info', soft: 'bg-info/10', dot: 'bg-info' },
  rose: { text: 'text-danger', soft: 'bg-danger/10', dot: 'bg-danger' }
};

const PRIORITY_DOT = { high: 'bg-danger', medium: 'bg-warning', low: 'bg-info' };

/** Compact, number-forward KPI tile. The whole tile is a click target. */
function StatTile({ icon: Icon, label, count = 0, tone = 'sage', to, onNavigate }) {
  const t = TONES[tone];
  return (
    <button
      type="button"
      onClick={() => to && onNavigate(to)}
      className="group flex items-center gap-3 rounded-xl border border-border bg-surface-elevated p-3.5 text-left transition-all hover:border-brand/40 hover:shadow-elevated"
    >
      <span className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-lg ${t.soft} ${t.text}`}>
        <Icon className="h-5 w-5" strokeWidth={1.75} />
      </span>
      <div className="min-w-0">
        <div className="flex items-baseline gap-1.5">
          <span className="text-xl font-semibold tabular-nums leading-none text-ink">{count}</span>
        </div>
        <p className="mt-0.5 truncate text-xs text-ink-muted">{label}</p>
      </div>
    </button>
  );
}

/** A content block, natural height. Header doubles as the section link. */
function Block({ icon: Icon, title, count, tone = 'sage', to, children, onNavigate, empty, className }) {
  const t = TONES[tone];
  return (
    <Card padded={false} className={`flex flex-col ${className || ''}`}>
      <button
        type="button"
        onClick={() => to && onNavigate(to)}
        className="group flex items-center justify-between gap-2 border-b border-border px-4 py-3 text-left transition-colors hover:bg-surface-subtle/60"
      >
        <div className="flex items-center gap-2.5">
          <span className={`flex h-7 w-7 items-center justify-center rounded-lg ${t.soft} ${t.text}`}>
            <Icon className="h-4 w-4" strokeWidth={1.75} />
          </span>
          <h2 className="text-sm font-semibold text-ink">{title}</h2>
          {typeof count === 'number' && count > 0 && (
            <span className={`rounded-full ${t.soft} px-1.5 py-0.5 text-[11px] font-semibold tabular-nums ${t.text}`}>{count}</span>
          )}
        </div>
        <ChevronRight className="h-4 w-4 text-ink-faint transition-transform group-hover:translate-x-0.5" />
      </button>
      <div className="flex-1 px-4 py-3">
        {children || <p className="text-sm text-ink-faint">{empty || 'Nulla da segnalare.'}</p>}
      </div>
    </Card>
  );
}

/** Dense, metadata-rich rows for task-shaped items. */
function TaskRows({ items, dot = 'priority', tone = 'sage', onNavigate, to }) {
  if (!items || items.length === 0) return null;
  return (
    <ul className="-my-1 flex flex-col divide-y divide-border/60">
      {items.slice(0, 5).map((it, i) => {
        const overdue = it.dueAt && new Date(it.dueAt) < new Date();
        const dotClass = dot === 'priority' ? (PRIORITY_DOT[it.priority] || 'bg-ink-faint') : TONES[tone].dot;
        return (
          <li
            key={it.id || i}
            onClick={() => to && onNavigate(to)}
            className="flex cursor-pointer items-center gap-2.5 py-2"
          >
            <span className={`h-1.5 w-1.5 shrink-0 rounded-full ${dotClass}`} />
            <span className="min-w-0 flex-1 truncate text-sm text-ink-muted">{it.title}</span>
            {it.dueAt && (
              <span className={`shrink-0 text-xs tabular-nums ${overdue ? 'font-medium text-danger' : 'text-ink-faint'}`}>
                {new Date(it.dueAt).toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit' })}
              </span>
            )}
          </li>
        );
      })}
    </ul>
  );
}

const QUICK_ACTIONS = [
  { label: 'Nuovo cliente', icon: UserPlus, to: '/admin/customers', perm: 'customers:create' },
  { label: 'Nuova attività', icon: ListPlus, to: '/admin/tasks', perm: 'tasks:create' },
  { label: 'Nuovo trasferimento', icon: ArrowLeftRight, to: '/admin/logistics', perm: 'logistics:create' },
  { label: 'Nuovo carico', icon: PackagePlus, to: '/admin/logistics', perm: 'logistics:create' }
];

export function OperationsCenterPage() {
  const navigate = useNavigate();
  const { user, hasPermission } = useAuth();
  const queryClient = useQueryClient();
  const go = (to) => navigate(to);

  const { data, isLoading } = useQuery({ queryKey: ['operations', 'summary'], queryFn: operationsApi.summary });
  const s = data || {};

  const canReplenish = hasPermission('logistics:create');
  const runReplenishment = useMutation({
    mutationFn: () => replenishmentApi.run(),
    onSuccess: (res) => {
      const n = (res && res.created) || 0;
      toast.success(n > 0 ? `${n} attività di riordino create.` : 'Nessuna nuova attività di riordino.');
      queryClient.invalidateQueries({ queryKey: ['operations', 'summary'] });
    },
    onError: () => toast.error('Impossibile generare le attività di riordino.')
  });

  const name = user && user.email ? user.email.split('@')[0] : '';
  const actions = QUICK_ACTIONS.filter((a) => hasPermission(a.perm));

  const stats = [
    { key: 'attention', icon: AlertTriangle, label: 'Da gestire', count: s.attention?.count, tone: 'rose', to: '/admin/tasks' },
    { key: 'alerts', icon: Sparkles, label: 'Alert AI', count: s.alerts?.count, tone: 'taupe', to: '/admin/tasks' },
    { key: 'followUps', icon: CalendarClock, label: 'Follow-up', count: s.followUps?.count, tone: 'slate', to: '/admin/customers' },
    { key: 'training', icon: GraduationCap, label: 'Formazione', count: s.training?.count, tone: 'sage', to: '/admin/tasks' },
    { key: 'logistics', icon: Truck, label: 'Logistica', count: s.logistics?.count, tone: 'amber', to: '/admin/logistics' }
  ];

  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-ink">Centro Operativo</h1>
          <p className="mt-1 text-sm text-ink-muted">{greeting()}{name ? `, ${name}` : ''}. Ecco cosa richiede attenzione oggi.</p>
        </div>
        {actions.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {actions.map((a) => {
              const Icon = a.icon;
              return (
                <button key={a.label} type="button" onClick={() => go(a.to)} className="inline-flex items-center gap-1.5 rounded-lg border border-border bg-surface-elevated px-3 py-1.5 text-sm text-ink hover:border-brand/40 hover:bg-brand-soft">
                  <Icon className="h-4 w-4 text-brand" strokeWidth={1.75} />{a.label}
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* KPI strip: the at-a-glance cockpit */}
      {isLoading ? (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
          {Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} className="h-[68px] w-full rounded-xl" />)}
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
          {stats.map((st) => <StatTile key={st.key} {...st} onNavigate={go} />)}
        </div>
      )}

      {isLoading ? (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
          {Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-56 w-full rounded-xl" />)}
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
          {/* Left: actionable work, the widest column */}
          <div className="flex flex-col gap-4 lg:col-span-2">
            <Block icon={ClipboardList} title="Da gestire ora" tone="rose" count={s.attention?.count} to="/admin/tasks" onNavigate={go} empty="Niente di urgente. Tutto sotto controllo.">
              {s.attention?.items?.length > 0 && <TaskRows items={s.attention.items} dot="priority" onNavigate={go} to="/admin/tasks" />}
            </Block>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <Block icon={Sparkles} title="Alert AI" tone="taupe" count={s.alerts?.count} to="/admin/tasks" onNavigate={go} empty="Nessun alert dalle conversazioni.">
                {s.alerts?.items?.length > 0 && <TaskRows items={s.alerts.items} dot="tone" tone="taupe" onNavigate={go} to="/admin/tasks" />}
              </Block>

              <Block icon={GraduationCap} title="Formazione" tone="sage" count={s.training?.count} to="/admin/tasks" onNavigate={go} empty="Nessuna attività formativa.">
                {s.training?.items?.length > 0 && <TaskRows items={s.training.items} dot="tone" tone="sage" onNavigate={go} to="/admin/tasks" />}
              </Block>
            </div>

            <Block icon={CalendarClock} title="Follow-up in scadenza" tone="slate" count={s.followUps?.count} to="/admin/customers" onNavigate={go} empty="Nessun richiamo in scadenza.">
              {s.followUps?.items?.length > 0 && (
                <ul className="-my-1 flex flex-col divide-y divide-border/60">
                  {s.followUps.items.slice(0, 5).map((f, i) => (
                    <li key={f.id || i} onClick={() => go('/admin/customers')} className="flex cursor-pointer items-center gap-2.5 py-2">
                      <span className={`h-1.5 w-1.5 shrink-0 rounded-full ${TONES.slate.dot}`} />
                      <span className="min-w-0 flex-1 truncate text-sm text-ink-muted">{f.label}</span>
                      {f.date && <span className="shrink-0 text-xs tabular-nums text-ink-faint">{new Date(f.date).toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit' })}</span>}
                    </li>
                  ))}
                </ul>
              )}
            </Block>
          </div>

          {/* Right: logistics + activity stream */}
          <div className="flex flex-col gap-4">
            <Block icon={Truck} title="Logistica" tone="amber" count={s.logistics?.count} to="/admin/logistics" onNavigate={go} empty="Nessun movimento in sospeso.">
              {(s.logistics?.transfers?.length > 0 || s.logistics?.loads?.length > 0) && (
                <ul className="-my-1 flex flex-col divide-y divide-border/60">
                  {[...(s.logistics?.transfers || []), ...(s.logistics?.loads || [])].slice(0, 6).map((x, i) => (
                    <li key={x.id || i} onClick={() => go('/admin/logistics')} className="flex cursor-pointer items-center gap-2.5 py-2">
                      <span className={`h-1.5 w-1.5 shrink-0 rounded-full ${TONES.amber.dot}`} />
                      <span className="min-w-0 flex-1 truncate text-sm text-ink-muted">
                        {x.from ? `${x.from} → ${x.to}` : `Carico ${x.store || ''}`}
                      </span>
                      <span className="shrink-0 rounded-full bg-surface-subtle px-1.5 py-0.5 text-[11px] text-ink-faint">{x.status}</span>
                    </li>
                  ))}
                </ul>
              )}

              {s.logistics?.lowStock?.count > 0 && (
                <div className="mt-3 border-t border-border/60 pt-3">
                  <div className="mb-1.5 flex items-center justify-between">
                    <span className="inline-flex items-center gap-1.5 text-xs font-medium text-ink-muted">
                      <PackageSearch className="h-3.5 w-3.5" /> Scorte basse ({s.logistics.lowStock.count})
                    </span>
                    {canReplenish && (
                      <button
                        type="button"
                        onClick={() => runReplenishment.mutate()}
                        disabled={runReplenishment.isPending}
                        className="text-[11px] font-medium text-brand hover:text-brand-hover disabled:opacity-50"
                      >
                        Genera attività
                      </button>
                    )}
                  </div>
                  <ul className="-my-1 flex flex-col divide-y divide-border/60">
                    {s.logistics.lowStock.items.slice(0, 5).map((it) => (
                      <li key={it.productId} className="flex items-center gap-2.5 py-2">
                        <span className="min-w-0 flex-1 truncate text-sm text-ink-muted">{it.name}</span>
                        <span className="shrink-0 text-[11px] text-ink-faint">
                          {it.quantity} pz{it.daysOfStock != null ? ` · ~${it.daysOfStock}gg` : ''} · +{it.suggestedQty}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </Block>

            <Block icon={History} title="Attività recenti" tone="slate" to="/admin/tasks" onNavigate={go} empty="Nessuna attività recente.">
              {s.recent?.length > 0 && (
                <ul className="flex flex-col gap-3">
                  {s.recent.slice(0, 7).map((r, i) => (
                    <li key={r.id || i} className="flex items-start gap-2.5">
                      <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-border" />
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm text-ink-muted">{r.label}</p>
                        <p className="text-xs text-ink-faint">{relTime(r.at)}</p>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </Block>
          </div>
        </div>
      )}
    </div>
  );
}
